import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const CHECKLIST = [
  {
    id: 'profile',
    label: 'Set up your profile',
    description: 'Add your name, niche, and income goal',
    panel: 'profile',
    auto: true,
  },
  {
    id: 'project',
    label: 'Add your first project',
    description: 'Track active work in the Projects panel',
    panel: 'projects',
    auto: true,
  },
  {
    id: 'pitch',
    label: 'Log your first pitch',
    description: 'Keep tabs on outreach in the Pitch Tracker',
    panel: 'pitches',
    auto: true,
  },
  {
    id: 'income',
    label: 'Record your first payment',
    description: 'Log income and track what you\'re owed',
    panel: 'income',
    auto: true,
  },
  {
    id: 'rate',
    label: 'Calculate your rate',
    description: 'Save a rate profile to find out what you should be charging',
    panel: 'calculator',
    auto: true,
  },
  {
    id: 'video',
    label: 'Watch the tutorial video',
    description: 'A quick walkthrough of everything Writer OS can do',
    url: 'https://youtu.be/7h_qZpXDr8w', // Replace with real URL
    auto: false,
  },
];

export default function WelcomeModal({ session, theme, onClose, onNavigate, onAllDone }) {
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProgress() {
      const userId = session.user.id;

      // Load saved manual progress
      const { data: settingsData } = await supabase
        .from('settings')
        .select('tour_progress')
        .eq('user_id', userId)
        .single();

      const saved = settingsData?.tour_progress || {};

      // Auto-detect completed steps
      const [projects, pitches, income, rates, profile] = await Promise.all([
        supabase.from('projects').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('pitches').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('income').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('rate_profiles').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('profiles').select('display_name').eq('user_id', userId).single(),
      ]);

      setChecked({
        ...saved,
        project: (projects.count || 0) > 0,
        pitch: (pitches.count || 0) > 0,
        income: (income.count || 0) > 0,
        rate: (rates.count || 0) > 0,
        profile: !!(profile.data?.display_name),
      });

      setLoading(false);
    }

    loadProgress();
  }, [session]);

  async function handleManualCheck(id) {
    const updated = { ...checked, [id]: !checked[id] };
    setChecked(updated);

    // Save only manual fields
    const manualKeys = CHECKLIST.filter(i => !i.auto).map(i => i.id);
    const toSave = {};
    manualKeys.forEach(k => { toSave[k] = updated[k] || false; });

    await supabase
      .from('settings')
      .upsert({ user_id: session.user.id, tour_progress: toSave }, { onConflict: 'user_id' });
  }

  async function handleClose() {
    await supabase
      .from('settings')
      .upsert({ user_id: session.user.id, has_seen_tour: true }, { onConflict: 'user_id' });
    onClose();
  }

  function handleGoTo(item) {
    if (item.url) {
      window.open(item.url, '_blank');
      handleManualCheck(item.id);
      return;
    }
    if (item.panel) {
      onNavigate(item.panel);
      handleClose();
    }
  }

  const allDone = CHECKLIST.every(item => checked[item.id]);
  const completedCount = CHECKLIST.filter(item => checked[item.id]).length;

  useEffect(() => {
    if (allDone && onAllDone) onAllDone();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  const t = theme || {};

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: t.surface || '#ffffff',
        border: `1px solid ${t.border || '#e0d8c8'}`,
        borderRadius: '16px',
        width: '100%', maxWidth: '520px',
        padding: '32px',
        color: t.text || '#0a2342',
        boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '6px' }}>
            Welcome to Writer OS 👋
          </div>
          <div style={{ fontSize: '14px', opacity: 0.65, lineHeight: 1.5 }}>
            Here's a quick checklist to get you set up. Complete these steps
            and you'll have everything running in about 10 minutes.
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ margin: '20px 0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', opacity: 0.6, marginBottom: '6px' }}>
            <span>{completedCount} of {CHECKLIST.length} completed</span>
            {allDone && <span style={{ color: '#4ade80' }}>You're all set! 🎉</span>}
          </div>
          <div style={{ height: '6px', borderRadius: '3px', background: t.border || '#e0d8c8', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${(completedCount / CHECKLIST.length) * 100}%`,
              background: t.accent || '#d9ad2b',
              borderRadius: '3px',
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>

        {/* Checklist */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0', opacity: 0.5 }}>Checking your progress...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
            {CHECKLIST.map(item => {
              const done = !!checked[item.id];
              return (
                <div
                  key={item.id}
                  onClick={() => (item.url || item.panel) ? handleGoTo(item) : handleManualCheck(item.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '12px 14px', borderRadius: '10px',
                    background: done ? (t.accent ? `${t.accent}18` : '#d9ad2b18') : (t.surfaceAlt || '#f5f0e8'),
                    border: `1px solid ${done ? (t.accent || '#d9ad2b') + '44' : (t.border || '#e0d8c8')}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {/* Checkbox */}
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${done ? (t.accent || '#d9ad2b') : (t.border || '#e0d8c8')}`,
                    background: done ? (t.accent || '#d9ad2b') : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s',
                  }}>
                    {done && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px', fontWeight: 500,
                      opacity: done ? 0.5 : 1,
                      textDecoration: done ? 'line-through' : 'none',
                    }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.45, marginTop: '1px' }}>
                      {item.description}
                    </div>
                  </div>

                  {/* Arrow */}
                  {!done && (
                    <div style={{ opacity: 0.35, fontSize: '14px' }}>→</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleClose}
            style={{
              background: t.accent || '#d9ad2b',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {allDone ? "You're all set! Let's go 🎉" : "Got it, let's go →"}
          </button>
        </div>
      </div>
    </div>
  );
}